import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProjectCreateDialog, ProjectTable } from "./components";
import {
  SearchFilterBar,
  FilterSettings,
} from "@/components/ui/search-filter-bar";
import { PageHeading } from "@/components/ui/page-heading";
import { apiService } from "@/lib/api-service";
import { Project, ProjectFilters, SalesMember } from "@/types/project.types";
import {
  DEFAULT_DEPARTMENTS,
  ProjectStatus,
} from "@employee-management/common";

export const ProjectsListView = () => {
  const [filters, setFilters] = useState<ProjectFilters>({
    page: 1,
    limit: 10,
    search: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    salesMemberId: "",
    handlerMemberId: "",
    startDateFrom: "",
    startDateTo: "",
    status: ProjectStatus.ACTIVE,
  });

  const { data: salesMembers } = useQuery({
    queryKey: ["sales-members"],
    queryFn: async () =>
      apiService.get<SalesMember[]>("/employees/sales-members"),
  });

  const {
    data: projectsResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", JSON.stringify(filters)],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.search) params.append("search", filters.search);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (filters.salesMemberId && filters.salesMemberId !== "all")
        params.append("salesMemberId", filters.salesMemberId);
      if (filters.handlerMemberId && filters.handlerMemberId !== "all")
        params.append("handlerMemberId", filters.handlerMemberId);
      if (filters.startDateFrom)
        params.append("startDateFrom", filters.startDateFrom);
      if (filters.startDateTo)
        params.append("startDateTo", filters.startDateTo);
      if (filters.status) params.append("status", filters.status);

      const res = await apiService.getPaginated<Project>(
        `/projects?${params.toString()}`
      );
      return res;
    },
  });

  const handleFilterChange = (filterSettings: FilterSettings) => {
    setFilters((prev) => ({
      ...prev,
      search: filterSettings.searchTerm,
      salesMemberId:
        (filterSettings.filters["salesTeamMemberRef.id"] as string) || "",
      handlerMemberId:
        (filterSettings.filters["handlerMemberRef.id"] as string) || "",
      startDateFrom: (filterSettings.filters.startDate as any)?.min || "",
      startDateTo: (filterSettings.filters.startDate as any)?.max || "",
      status: (filterSettings.filters.status as string) || "",
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleSortChange = (sortBy: string, sortOrder: "asc" | "desc") => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1, // Reset to first page when sorting changes
    }));
  };

  // Filter options for projects
  const filterOptions = useMemo(
    () => [
      {
        key: "handlerMemberRef.id",
        label: "Project Supervisor",
        type: "select" as const,
        options: [
          ...(salesMembers || [])
            .filter((member) => member.department === DEFAULT_DEPARTMENTS[0])
            .map((member) => ({
              value: member.id,
              label: member.name,
            })),
        ],
      },
      {
        key: "salesTeamMemberRef.id",
        label: "Project Manager",
        type: "select" as const,
        options: [
          ...(salesMembers || [])
            .filter((member) => member.department === DEFAULT_DEPARTMENTS[1])
            .map((member) => ({
              value: member.id,
              label: member.name,
            })),
        ],
      },
      {
        key: "startDate",
        label: "Start Date",
        type: "range" as const,
        rangeType: "date" as const,
      },
      {
        key: "status",
        label: "Status",
        type: "select" as const,
        options: [
          { value: ProjectStatus.ACTIVE, label: "Active" },
          { value: ProjectStatus.COMPLETED, label: "Completed" },
          { value: ProjectStatus.CANCELLED, label: "Cancelled" },
          { value: ProjectStatus.ON_HOLD, label: "On Hold" },
        ],
        defaultValue: ProjectStatus.ACTIVE,
      },
    ],
    [salesMembers]
  );

  return (
    <div className="space-y-6">
      <PageHeading
        title="Projects"
        subtitle="Manage your projects and track their progress."
        actions={<ProjectCreateDialog />}
      />

      <SearchFilterBar
        initialValue={{
          searchTerm: "",
          filters: {
            status: ProjectStatus.ACTIVE,
          },
        }}
        filterOptions={filterOptions}
        placeholder="Search projects by name, client, or internal ID..."
        className="mb-6"
        onFilterChange={handleFilterChange}
      />

      {isLoading && <div>Loading projects...</div>}
      {isError && <div className="text-red-500">Failed to load projects.</div>}

      {!isLoading && !isError && projectsResponse && (
        <ProjectTable
          data={projectsResponse.data || []}
          pagination={projectsResponse.pagination}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          currentSort={{
            sortBy: filters.sortBy!,
            sortOrder: filters.sortOrder!,
          }}
        />
      )}
    </div>
  );
};
